import axios from 'axios';
import { parseProblem } from '@shared/api-errors';
import { getCorrelationId } from '../lib/correlation';
import { logger } from '../lib/logger';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token, tenant header, and correlation ID
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const tenantSlug = localStorage.getItem('tenantSlug') || 'demo';
    config.headers['X-Tenant-Slug'] = tenantSlug;
    config.headers['X-Correlation-ID'] = getCorrelationId();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Shared refresh promise to prevent concurrent refresh calls
let refreshPromise: Promise<string> | null = null;

function clearAuthAndRedirect() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

// Response interceptor - handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(
              `${API_URL}/api/auth/refresh-token`,
              { refreshToken },
              {
                headers: {
                  'X-Tenant-Slug': localStorage.getItem('tenantSlug') || 'demo',
                },
              }
            )
            .then((res) => {
              const { accessToken, refreshToken: newRefreshToken } = res.data;
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);
              return accessToken;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const accessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
    }

    // Normalize to ApiError when the server sent problem+json (expected business errors)
    const apiErr = parseProblem(error);
    if (apiErr) return Promise.reject(apiErr);

    // Non-problem+json — unexpected; log for investigation
    logger.warn('API request failed', {
      url: error.config?.url,
      status: error.response?.status,
      correlationId: getCorrelationId(),
    });

    return Promise.reject(error);
  }
);

export { api };
